import React, {
    useContext 
} from 'react';
import {
    Box, useTheme 
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import {
    FormContext 
} from '../../../contexts/form';

type Props = {
    currentPosition: number,
    sectionKey: string,
    colors?: any
};

export default function ProgressBar({
    currentPosition,
    sectionKey,
    colors
}: Props): JSX.Element {
    const formContext = useContext(FormContext);
    const theme = useTheme();
    const useStyles = makeStyles(() => ({
        progress: {

        },
        sections: {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            marginBottom: '30px'
        },
        section: {
            display: 'flex',
            flexDirection: 'row',
            columnGap: '20px',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            width: '24px',
            height: '24px'
        },
        icon: {
            display: 'flex',
            width: '50%',
            height: '50%',
            fill: theme.palette.white,
            stroke: 'none'
        },
        lines: {
            padding: '0 24px',
            display: 'flex',
            flexDirection: 'row',
            columnGap: '24px',
            transform: 'translate(0, 14px)',
            height: '2px'
        },
        progressLines: {
            padding: '0 24px',
            display: 'grid',
            columnGap: '24px',
            transform: 'translate(0, 12px)',
            height: '2px'
        },
        line: {
            width: '100%',
            backgroundColor: '#EBEBEB',
        }
    }));
    const classes = useStyles();
    const currentSectionIndex = formContext.sections.filter(section => section.inProgressBar).findIndex(section => section.key === sectionKey);

    const CheckMarkIcon = () =>(
        <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <circle
                cx="12"
                cy="12"
                r="11.5"
                fill="#5C5C5C"
                stroke="#5C5C5C"
            />
            <g clipPath="url(#clip0_4087_7431)">
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M17.6631 8.74005C18.1235 9.18268 18.1104 9.8881 17.6337 10.3157L11.4219 15.8871C10.9649 16.297 10.2428 16.305 9.77537 15.9053L6.38714 13.0082C5.89961 12.5913 5.86832 11.8864 6.31726 11.4337C6.76619 10.981 7.52534 10.9519 8.01287 11.3688L10.5686 13.554L15.9663 8.71273C16.443 8.28518 17.2027 8.29741 17.6631 8.74005Z"
                    fill="white"
                />
            </g>
            <defs>
                <clipPath id="clip0_4087_7431">
                    <rect
                        width="12"
                        height="12"
                        fill="white"
                        transform="translate(6 6)"
                    />
                </clipPath>
            </defs>
        </svg>
    );

    return (
        <Box>
            <Box className={classes.lines}>
                {formContext.sections.filter(section => section.inProgressBar).map((_, index) => {
                    if (index === 0) return null;
                    return <Box
                        key={index}
                        className={classes.line}
                        style={{
                            backgroundColor: colors?.inactive
                        }}
                    />;
                })}
            </Box>
            <Box
                className={classes.progressLines}
                style={{
                    gridTemplateColumns: `repeat(${formContext.sections.filter(section => section.inProgressBar).length - 1}, 1fr)` 
                }}
            >
                {formContext.sections.filter(section => section.inProgressBar).map((section, index) => {
                    let width = 0;
                    let backgroundColor = theme.palette.neutral.light;
                    if (index === currentSectionIndex) {
                        backgroundColor = colors?.active || section.colors.main;
                        width = Math.min(currentPosition / section.length * 100, 100);
                    } else if (index < currentSectionIndex) {
                        backgroundColor = theme.palette.neutral['400'];
                        width = 100;
                    }

                    return (
                        <Box
                            key={index}
                            style={{
                                height: '2px',
                                width: `${width}%`,
                                backgroundColor
                            }}
                        />
                    );
                })}
            </Box>
            <Box className={classes.sections}>
                {formContext.sections.filter(section => section.inProgressBar).map((section, index) => {
                    if (index < currentSectionIndex) {
                        return <CheckMarkIcon key={index} />;
                    } else if (index === currentSectionIndex) {
                        return (
                            <Box
                                key={index}
                                className={classes.section}
                                style={{
                                    backgroundColor: colors?.active || section.colors.main 
                                }}
                            >
                                <Box
                                    className={classes.icon}
                                    style={{
                                        fill: colors?.activeIcon || section.colors.buttonArrow
                                    }}
                                >
                                    {section.icon}
                                </Box>
                            </Box>
                        );
                    }

                    return (
                        <Box
                            key={index}
                            className={classes.section}
                            style={{
                                backgroundColor: theme.palette.neutral.light 
                            }}
                        >
                            <Box
                                className={classes.icon}
                            >
                                {section.icon}
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}
